<template>
  <div class="w-full">
    <ReportHeader :header-title="$t('BULK_SUMMARY_REPORTS.HEADER')">
      <V4Button
        :label="$t('BULK_SUMMARY_REPORTS.GENERATE_SUMMARY')"
        icon="i-ph-magic-wand"
        size="sm"
        :loading="isGenerating"
        :disabled="!canGenerate"
        @click="generateBulkSummary"
      />
    </ReportHeader>

    <!-- Filters Card -->
    <div class="bg-white dark:bg-slate-900 border border-slate-75 dark:border-slate-800 rounded-lg p-6 mb-6">
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        <!-- Date Range -->
        <div>
          <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
            {{ $t('BULK_SUMMARY_REPORTS.FILTERS.DATE_RANGE') }}
          </label>
          <select v-model="selectedDateRange" class="form-select w-full">
            <option value="last_7_days">
              {{ $t('BULK_SUMMARY_REPORTS.DATE_RANGES.LAST_7_DAYS') }}
            </option>
            <option value="last_30_days">
              {{ $t('BULK_SUMMARY_REPORTS.DATE_RANGES.LAST_30_DAYS') }}
            </option>
            <option value="custom">
              {{ $t('BULK_SUMMARY_REPORTS.DATE_RANGES.CUSTOM') }}
            </option>
          </select>
        </div>

        <!-- Custom Date Range -->
        <div v-if="selectedDateRange === 'custom'" class="sm:col-span-2 lg:col-span-3">
          <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
            {{ $t('BULK_SUMMARY_REPORTS.FILTERS.CUSTOM_RANGE') }}
          </label>
          <div class="flex gap-3">
            <input
              v-model="customDateFrom"
              type="date"
              class="form-input flex-1"
            />
            <input
              v-model="customDateTo"
              type="date"
              class="form-input flex-1"
            />
          </div>
        </div>

        <!-- Inbox Filter -->
        <div>
          <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
            {{ $t('BULK_SUMMARY_REPORTS.FILTERS.INBOX') }}
          </label>
          <select v-model="selectedInbox" class="form-select w-full">
            <option value="">
              {{ $t('BULK_SUMMARY_REPORTS.FILTERS.ALL_INBOXES') }}
            </option>
            <option
              v-for="inbox in inboxes"
              :key="inbox.id"
              :value="inbox.id"
            >
              {{ inbox.name }}
            </option>
          </select>
        </div>

        <!-- Status Filter -->
        <div>
          <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
            {{ $t('BULK_SUMMARY_REPORTS.FILTERS.STATUS') }}
          </label>
          <select v-model="selectedStatus" class="form-select w-full">
            <option value="">
              {{ $t('BULK_SUMMARY_REPORTS.FILTERS.ALL_STATUSES') }}
            </option>
            <option value="open">
              {{ $t('BULK_SUMMARY_REPORTS.STATUS_OPTIONS.OPEN') }}
            </option>
            <option value="resolved">
              {{ $t('BULK_SUMMARY_REPORTS.STATUS_OPTIONS.RESOLVED') }}
            </option>
            <option value="pending">
              {{ $t('BULK_SUMMARY_REPORTS.STATUS_OPTIONS.PENDING') }}
            </option>
          </select>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="isLoading" class="text-center py-16">
      <div
        class="animate-spin rounded-full h-8 w-8 border-b-2 border-woot-500 mx-auto mb-4"
      />
      <p class="text-slate-600 dark:text-slate-400">
        {{ $t('BULK_SUMMARY_REPORTS.LOADING') }}
      </p>
    </div>

    <!-- Error State -->
    <div
      v-else-if="error"
      class="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6"
    >
      <div class="flex">
        <fluent-icon
          icon="error-circle"
          class="text-red-400 mr-3 mt-0.5"
          size="20"
        />
        <div>
          <h3 class="text-sm font-medium text-red-800 dark:text-red-200">
            {{ $t('BULK_SUMMARY_REPORTS.ERROR.TITLE') }}
          </h3>
          <div class="mt-2 text-sm text-red-700 dark:text-red-300">
            <p>{{ error }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Results Table -->
    <div
      v-else-if="conversationResults.length > 0"
      class="bg-white dark:bg-slate-900 border border-slate-75 dark:border-slate-800 rounded-lg overflow-hidden"
    >
      <!-- Table Header -->
      <div class="px-6 py-4 border-b border-slate-75 dark:border-slate-800">
        <h3 class="text-lg font-medium text-slate-900 dark:text-slate-100">
          {{
            $t('BULK_SUMMARY_REPORTS.RESULTS.TITLE', {
              count: pagination.total_count,
            })
          }}
        </h3>
      </div>

      <!-- Table Content -->
      <div class="overflow-x-auto">
        <table class="min-w-full woot-table">
          <thead class="bg-slate-50 dark:bg-slate-800">
            <tr>
              <th class="px-6 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider w-32">
                {{ $t('BULK_SUMMARY_REPORTS.TABLE.HEADER.INBOX') }}
              </th>
              <th class="px-6 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider w-48">
                {{ $t('BULK_SUMMARY_REPORTS.TABLE.HEADER.CONTACT') }}
              </th>
              <th class="px-6 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                {{ $t('BULK_SUMMARY_REPORTS.TABLE.HEADER.SUMMARY') }}
              </th>
              <th class="px-6 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider w-28">
                {{ $t('BULK_SUMMARY_REPORTS.TABLE.HEADER.STATUS') }}
              </th>
            </tr>
          </thead>
          <tbody class="bg-white dark:bg-slate-900 divide-y divide-slate-75 dark:divide-slate-800">
            <tr
              v-for="conversation in conversationResults"
              :key="conversation.id"
              class="hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors duration-150"
            >
              <!-- Inbox -->
              <td class="px-6 py-4 whitespace-nowrap">
                <div class="text-sm font-medium text-slate-900 dark:text-slate-100">
                  {{ conversation.inbox_name }}
                </div>
              </td>

              <!-- Contact Name -->
              <td class="px-6 py-4">
                <div class="text-sm font-medium text-slate-900 dark:text-slate-100">
                  {{ conversation.contact_name || '-' }}
                </div>
              </td>

              <!-- Summary -->
              <td class="px-6 py-4">
                <div class="text-sm text-slate-700 dark:text-slate-300 leading-relaxed">
                  {{ conversation.summary }}
                </div>
              </td>

              <!-- Status -->
              <td class="px-6 py-4 whitespace-nowrap">
                <span
                  :class="getStatusClass(conversation.status)"
                  class="inline-flex px-2 py-1 text-xs font-medium rounded-full"
                >
                  {{ getStatusLabel(conversation.status) }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div
        v-if="pagination.total_pages > 1"
        class="bg-white dark:bg-slate-900 px-6 py-4 border-t border-slate-75 dark:border-slate-800"
      >
        <div class="flex items-center justify-between">
          <!-- Mobile pagination -->
          <div class="flex justify-between sm:hidden w-full">
            <button
              :disabled="pagination.current_page === 1"
              class="relative inline-flex items-center px-4 py-2 border border-slate-300 dark:border-slate-600 text-sm font-medium rounded-md text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              @click="changePage(pagination.current_page - 1)"
            >
              {{ $t('BULK_SUMMARY_REPORTS.PAGINATION.PREVIOUS') }}
            </button>
            <button
              :disabled="pagination.current_page === pagination.total_pages"
              class="ml-3 relative inline-flex items-center px-4 py-2 border border-slate-300 dark:border-slate-600 text-sm font-medium rounded-md text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              @click="changePage(pagination.current_page + 1)"
            >
              {{ $t('BULK_SUMMARY_REPORTS.PAGINATION.NEXT') }}
            </button>
          </div>
          
          <!-- Desktop pagination -->
          <div class="hidden sm:flex sm:items-center sm:justify-between w-full">
            <div>
              <p class="text-sm text-slate-700 dark:text-slate-300">
                {{ $t('BULK_SUMMARY_REPORTS.PAGINATION.SHOWING') }}
                <span class="font-medium">{{ paginationStart }}</span>
                {{ $t('BULK_SUMMARY_REPORTS.PAGINATION.TO') }}
                <span class="font-medium">{{ paginationEnd }}</span>
                {{ $t('BULK_SUMMARY_REPORTS.PAGINATION.OF') }}
                <span class="font-medium">{{ pagination.total_count }}</span>
                {{ $t('BULK_SUMMARY_REPORTS.PAGINATION.RESULTS') }}
              </p>
            </div>
            <div>
              <nav class="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                <button
                  :disabled="pagination.current_page === 1"
                  class="relative inline-flex items-center px-3 py-2 rounded-l-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm font-medium text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  @click="changePage(pagination.current_page - 1)"
                >
                  <fluent-icon icon="chevron-left" size="16" />
                </button>
                
                <button
                  v-for="page in visiblePages"
                  :key="page"
                  :class="[
                    'relative inline-flex items-center px-4 py-2 border text-sm font-medium transition-colors',
                    page === pagination.current_page
                      ? 'bg-woot-50 dark:bg-woot-800 border-woot-500 dark:border-woot-600 text-woot-600 dark:text-woot-400'
                      : 'bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700'
                  ]"
                  @click="changePage(page)"
                >
                  {{ page }}
                </button>
                
                <button
                  :disabled="pagination.current_page === pagination.total_pages"
                  class="relative inline-flex items-center px-3 py-2 rounded-r-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm font-medium text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  @click="changePage(pagination.current_page + 1)"
                >
                  <fluent-icon icon="chevron-right" size="16" />
                </button>
              </nav>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div v-else class="text-center py-16">
      <fluent-icon
        icon="chat-bubbles"
        size="48"
        class="text-slate-300 dark:text-slate-600 mx-auto mb-4"
      />
      <h3 class="text-lg font-medium text-slate-900 dark:text-slate-100 mb-2">
        {{ $t('BULK_SUMMARY_REPORTS.EMPTY_STATE.TITLE') }}
      </h3>
      <p class="text-slate-500 dark:text-slate-400 max-w-md mx-auto">
        {{ $t('BULK_SUMMARY_REPORTS.EMPTY_STATE.MESSAGE') }}
      </p>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import ReportHeader from './components/ReportHeader.vue';
import V4Button from '../../../../components-next/button/Button.vue';
import { useAlert } from 'dashboard/composables';

export default {
  name: 'BulkSummaryReports',
  components: {
    ReportHeader,
    V4Button,
  },

  data() {
    return {
      selectedDateRange: 'last_7_days',
      customDateFrom: '',
      customDateTo: '',
      selectedInbox: '',
      selectedStatus: '',

      conversationResults: [],
      pagination: {
        current_page: 1,
        per_page: 50,
        total_count: 0,
        total_pages: 0,
      },

      isLoading: false,
      isGenerating: false,
      error: null,

      inboxes: [],
    };
  },

  computed: {
    ...mapGetters(['getCurrentAccountId']),

    canGenerate() {
      if (this.selectedDateRange === 'custom') {
        return this.customDateFrom && this.customDateTo;
      }
      return true;
    },

    paginationStart() {
      return (this.pagination.current_page - 1) * this.pagination.per_page + 1;
    },

    paginationEnd() {
      return Math.min(
        this.pagination.current_page * this.pagination.per_page,
        this.pagination.total_count
      );
    },

    visiblePages() {
      const current = this.pagination.current_page;
      const total = this.pagination.total_pages;
      const delta = 2;
      const pages = [];

      for (
        let i = Math.max(1, current - delta);
        i <= Math.min(total, current + delta);
        i++
      ) {
        pages.push(i);
      }

      return pages;
    },
  },

  mounted() {
    this.loadInboxes();
    this.loadSavedFilters();
    // Auto-load data with saved/default filters
    this.generateBulkSummary();
  },

  methods: {
    async loadInboxes() {
      try {
        await this.$store.dispatch('inboxes/get');
        this.inboxes = this.$store.getters['inboxes/getInboxes'];
      } catch (error) {
        console.error('Error loading inboxes:', error);
      }
    },

    loadSavedFilters() {
      try {
        const saved = localStorage.getItem('bulkSummaryFilters');
        if (saved) {
          const filters = JSON.parse(saved);
          this.selectedDateRange = filters.selectedDateRange || 'last_7_days';
          this.customDateFrom = filters.customDateFrom || '';
          this.customDateTo = filters.customDateTo || '';
          this.selectedInbox = filters.selectedInbox || '';
          this.selectedStatus = filters.selectedStatus || '';
        }
      } catch (error) {
        console.error('Error loading saved filters:', error);
      }
    },

    saveFilters() {
      try {
        const filters = {
          selectedDateRange: this.selectedDateRange,
          customDateFrom: this.customDateFrom,
          customDateTo: this.customDateTo,
          selectedInbox: this.selectedInbox,
          selectedStatus: this.selectedStatus,
        };
        localStorage.setItem('bulkSummaryFilters', JSON.stringify(filters));
      } catch (error) {
        console.error('Error saving filters:', error);
      }
    },

    async generateBulkSummary() {
      if (!this.canGenerate) return;

      this.isGenerating = true;
      this.isLoading = true;
      this.error = null;

      // Save current filters for next visit
      this.saveFilters();

      try {
        const params = this.buildRequestParams();
        const accountId = this.getCurrentAccountId;

        const response = await window.axios.post(
          `/api/v1/accounts/${accountId}/summaries/bulk`,
          params
        );

        this.conversationResults = response.data.conversations;
        this.pagination = response.data.pagination;

        if (this.conversationResults.length === 0) {
          useAlert(this.$t('BULK_SUMMARY_REPORTS.NO_RESULTS'));
        } else {
          useAlert(this.$t('BULK_SUMMARY_REPORTS.SUCCESS'));
        }
      } catch (error) {
        console.error('Bulk summary error:', error);
        this.error =
          error.response?.data?.error ||
          this.$t('BULK_SUMMARY_REPORTS.ERROR.MESSAGE');
        useAlert(this.error);
      } finally {
        this.isGenerating = false;
        this.isLoading = false;
      }
    },

    async changePage(page) {
      if (page < 1 || page > this.pagination.total_pages) return;

      this.isLoading = true;
      try {
        const params = this.buildRequestParams(page);
        const accountId = this.getCurrentAccountId;

        const response = await window.axios.post(
          `/api/v1/accounts/${accountId}/summaries/bulk`,
          params
        );

        this.conversationResults = response.data.conversations;
        this.pagination = response.data.pagination;
      } catch (error) {
        console.error('Pagination error:', error);
        this.error =
          error.response?.data?.error ||
          this.$t('BULK_SUMMARY_REPORTS.ERROR.MESSAGE');
      } finally {
        this.isLoading = false;
      }
    },

    buildRequestParams(page = 1) {
      const params = {
        page,
        per_page: this.pagination.per_page,
      };

      // Date range
      if (this.selectedDateRange === 'custom') {
        params.date_from = this.customDateFrom;
        params.date_to = this.customDateTo;
      } else {
        const dateRanges = this.getDateRange(this.selectedDateRange);
        params.date_from = dateRanges.from;
        params.date_to = dateRanges.to;
      }

      // Filters
      if (this.selectedInbox) params.inbox_id = this.selectedInbox;
      if (this.selectedStatus) params.status = this.selectedStatus;

      return params;
    },

    getDateRange(range) {
      const today = new Date();
      const formatDate = date => date.toISOString().split('T')[0];

      switch (range) {
        case 'last_7_days':
          return {
            from: formatDate(
              new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000)
            ),
            to: formatDate(today),
          };
        case 'last_30_days':
          return {
            from: formatDate(
              new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000)
            ),
            to: formatDate(today),
          };
        default:
          return {
            from: formatDate(
              new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000)
            ),
            to: formatDate(today),
          };
      }
    },

    getStatusLabel(status) {
      const statusMap = {
        open: this.$t('BULK_SUMMARY_REPORTS.STATUS_OPTIONS.OPEN'),
        resolved: this.$t('BULK_SUMMARY_REPORTS.STATUS_OPTIONS.RESOLVED'),
        pending: this.$t('BULK_SUMMARY_REPORTS.STATUS_OPTIONS.PENDING'),
      };
      return statusMap[status] || status;
    },

    getStatusClass(status) {
      const statusClasses = {
        resolved: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300',
        pending: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300',
        open: 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300',
      };
      return statusClasses[status] || 'bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-slate-300';
    },

    formatDate(dateString) {
      const date = new Date(dateString);
      return date.toLocaleDateString('vi-VN', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    },

    async generateAllSummaries() {
      if (!this.conversationResults.length) return;

      this.isGenerating = true;
      try {
        const params = this.buildRequestParams();
        params.force_regenerate = true;
        const accountId = this.getCurrentAccountId;

        const response = await window.axios.post(
          `/api/v1/accounts/${accountId}/summaries/bulk`,
          params
        );

        this.conversationResults = response.data.conversations;
        useAlert(this.$t('BULK_SUMMARY_REPORTS.ALERTS.REGENERATE_ALL_SUCCESS'));
      } catch (error) {
        console.error('Error regenerating all summaries:', error);
        useAlert(this.$t('BULK_SUMMARY_REPORTS.ALERTS.REGENERATE_ALL_ERROR'));
      } finally {
        this.isGenerating = false;
      }
    },

    async generateIndividualSummary(conversation) {
      const conversationIndex = this.conversationResults.findIndex(
        c => c.id === conversation.id
      );
      
      if (conversationIndex === -1) return;

      this.conversationResults[conversationIndex].summary = 'Đang tạo tóm tắt...';

      try {
        const accountId = this.getCurrentAccountId;
        
        const response = await window.axios.post(
          `/api/v1/accounts/${accountId}/conversations/${conversation.id}/summaries`,
          { conversation_id: conversation.id }
        );

        this.conversationResults[conversationIndex].summary = response.data.summary;
        useAlert(this.$t('BULK_SUMMARY_REPORTS.ALERTS.GENERATE_SUCCESS'));
      } catch (error) {
        console.error('Error generating individual summary:', error);
        this.conversationResults[conversationIndex].summary = 
          'Lỗi khi tạo tóm tắt: ' + (error.response?.data?.error || error.message);
        useAlert(this.$t('BULK_SUMMARY_REPORTS.ALERTS.GENERATE_ERROR'));
      }
    },
  },
};
</script>

<style scoped>
.form-select,
.form-input {
  @apply block w-full px-3 py-2.5 border border-slate-200 dark:border-slate-700 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-woot-500 focus:border-woot-500 text-sm bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 transition-colors;
}

.form-select:hover,
.form-input:hover {
  @apply border-slate-300 dark:border-slate-600;
}

.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.line-clamp-3 {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.woot-table th {
  @apply font-semibold text-xs uppercase tracking-wider;
}

.woot-table td {
  @apply text-sm;
}
</style> 